import { StowagePlan } from 'src/types/ViewModel/Stowage/StowagePlan';
import { CargoContent } from 'src/types/ViewModel/Stowage/Manifest/CargoContent';
import { getAllTanks } from './tankPlanUtils';
import { TankPlan } from 'src/types/ViewModel/Vessel/TankPlan';
import { getTankContentForCondition } from './manifest/tankPlanConditionUtils';
import { RotationPortCall } from 'src/types/ViewModel/Stowage/Rotation/RotationPortCall';
import { RotationBerthCall } from 'src/types/ViewModel/Stowage/Rotation/RotationBerthCall';
import {
    ManifestPdfReportContent,
    REPORT_TYPE,
    REPORT_TYPE_ORIENTATION,
    ReportGenerationOptions,
    ReportSubType,
    TankplanReportData,
} from '../reportGenerator/ReportGeneratorTypes';
import { TankPlanCondition } from 'src/enums/stowage/tankPlanCondition';
import { getMarpolPollutionCategoryString } from './cargoOptionUtils';
import { getChartererName, getFormattedCargoNumber, isManualCargo } from './cargoOperationUtils';
import { Vessel } from 'src/types/ViewModel/Vessel/Vessel';
import { TankPlanReportGenerationData } from './reports/tankPlanImageGenerator';
import { getCargoTemperature, getCorrectedVoyageTemperatureRange } from './stowageOperation/cargoTemperatureUtils';
import { getCorrectedLitreWeight } from './stowageOperation/volumeUtils';
import { Tank } from 'src/types/ViewModel/Vessel/Tank';
import { roundTo3Decimals } from '../numberHelper';
import { RotationCargoOperation } from 'src/types/ViewModel/Stowage/Rotation/RotationCargoOperation';
// NOTE: MIGRATION - Switched to unified ship figure calculation utility to retire legacy operationListView version.
import { getShipFigureForOperationType } from 'src/utils/stowage/stowageOperation/operationDisplayUtil';
import Decimal from 'decimal.js';

export const getCargoContentInTanksList = (
    tankPlan: TankPlan,
    activeStowagePlan: StowagePlan,
    selectedStowageRotationPortCall: RotationPortCall,
    selectedStowageRotationBerthCall: RotationBerthCall,
    selectedTankPlanCondition: number,
    selectedTanks: Tank[] | null = null
): CargoContent[] => {
    let _cargoInTanksList: CargoContent[] = [];
    const tanks = selectedTanks ? selectedTanks : getAllTanks(tankPlan);
    tanks.forEach((tank) => {
        const tankContent = getTankContentForCondition(
            activeStowagePlan,
            selectedStowageRotationPortCall,
            selectedStowageRotationBerthCall,
            selectedTankPlanCondition,
            tank
        );

        if (tankContent.cargoContents && tankContent.cargoContents.length > 0) {
            _cargoInTanksList = _cargoInTanksList.concat(tankContent.cargoContents);
        }
    });

    return _cargoInTanksList;
};

// Helper function to extract cargo number from formatted string for sorting
const extractCargoNumber = (formattedNumber: string): number => {
    // If the cargo number contains a dash, extract the part after the dash
    if (formattedNumber.includes('-')) {
        const parts = formattedNumber.split('-');
        return Number(parts[parts.length - 1]) || 0;
    }
    // Otherwise try to convert the whole string to a number
    return Number(formattedNumber) || 0;
};

const getOperationTypeLetter = (operationType: string): string => {
    if (operationType === 'Loading') {
        return 'L';
    } else if (operationType === 'Unloading') {
        return 'U';
    } else if (operationType === 'Transit') {
        return 'T';
    }
    return '';
};

// Helper function to build the cargo map from tanks
const buildCargoMapFromTanks = (
    tanks: Tank[],
    activeStowagePlan: StowagePlan,
    selectedStowageRotationPortCall: RotationPortCall,
    selectedStowageRotationBerthCall: RotationBerthCall,
    tankPlanCondition: TankPlanCondition
): Record<string, { cargoContent: CargoContent; tanks: Set<string>; operations: RotationCargoOperation[] }> => {
    const cargoMap: Record<string, { cargoContent: CargoContent; tanks: Set<string>; operations: RotationCargoOperation[] }> = {};

    // Get all cargo operations based on the current view. Scope is now driven purely by which
    // berth call is passed in: a specific berth call scopes to just that berth's operations
    // (this now also applies to Arrival/Departure when a berth-scoped variant is requested);
    // no berth call (whole-port scope) aggregates operations across every berth at the port.
    let cargoOperations: RotationCargoOperation[] = [];

    if (selectedStowageRotationBerthCall) {
        cargoOperations = selectedStowageRotationBerthCall.cargoOperations || [];
    } else if (selectedStowageRotationPortCall) {
        // If only a port is selected, combine operations from all berth calls
        cargoOperations = selectedStowageRotationPortCall.berthCalls
            ? selectedStowageRotationPortCall.berthCalls.flatMap((berth) => berth.cargoOperations || [])
            : [];
    }

    // Filter cargo operations based on tank plan condition
    let operationType: string | null = null;
    let shouldIncludeRegularOperations = true;
    let shouldIncludeTransitOperations = false;
    let includeAllOperationTypes = false;

    // Determine which types of operations to include based on tank plan condition
    switch (tankPlanCondition) {
        case TankPlanCondition.Arrival:
            operationType = 'Unloading';
            shouldIncludeTransitOperations = true;
            break;
        case TankPlanCondition.Departure:
            operationType = 'Loading';
            shouldIncludeTransitOperations = true;
            break;
        case TankPlanCondition.LoadingOperations:
            operationType = 'Loading';
            shouldIncludeTransitOperations = false;
            break;
        case TankPlanCondition.UnloadingOperations:
            operationType = 'Unloading';
            shouldIncludeTransitOperations = false;
            break;
        case TankPlanCondition.OperationsInCall:
            operationType = null; // Will process both loading and unloading
            includeAllOperationTypes = true;
            shouldIncludeTransitOperations = false;
            break;
        case TankPlanCondition.TransitOperations:
            operationType = null; // Not used for transit-only operations
            shouldIncludeRegularOperations = false;
            shouldIncludeTransitOperations = true;
            break;
        default:
            // For other conditions, include all operations
            operationType = null;
            shouldIncludeRegularOperations = true;
            shouldIncludeTransitOperations = false;
    }

    // Process regular (loading/unloading) operations if needed
    if (shouldIncludeRegularOperations) {
        // Filter cargo operations based on operation type (if not showing all)
        const filteredCargoOperations = includeAllOperationTypes
            ? cargoOperations // Include both loading and unloading for OperationsInCall
            : cargoOperations.filter((op) => operationType && op.operationType === operationType);

        // Process filtered cargo operations
        filteredCargoOperations.forEach((cargoOperation) => {
            if (!cargoOperation.cargoGuid) return;

            // Find the cargo from the activeStowagePlan.cargoes
            const cargo = activeStowagePlan.cargoes.find((c) => c.cargoGuid === cargoOperation.cargoGuid);
            if (!cargo) return;

            // Find stowage items for this cargo operation
            const stowageItems = activeStowagePlan.stowageItems.filter(
                (item) => item.cargoOperation?.cargoOperationGuid === cargoOperation.cargoOperationGuid
            );

            // Process each tank for this cargo operation
            stowageItems.forEach((stowageItem) => {
                // Skip if stowage item has no tank ID
                if (!stowageItem.tankId) return;

                // Find the tank
                const tank = tanks.find((t) => t.tankId === stowageItem.tankId);
                if (!tank) return;

                // Create a cargo content
                const cargoContent: CargoContent = {
                    cargoOperation: cargoOperation,
                    cargo: cargo,
                    weight: new Decimal(0),
                    weightInTransit: new Decimal(0),
                    billOfLadingWeight: new Decimal(0),
                    stowage: stowageItem,
                };

                // Generate a unique key for this cargo
                const cargoKey = getFormattedCargoNumber(cargo.voyageNumber, cargo.cargoNumber, isManualCargo(cargo));

                // Add to map or update existing entry
                if (!cargoMap[cargoKey]) {
                    cargoMap[cargoKey] = {
                        cargoContent: cargoContent,
                        tanks: new Set<string>(),
                        operations: [],
                    };
                }

                // Add tank to the list of tanks for this cargo
                cargoMap[cargoKey].tanks.add(tank.odfjellTankName);
                // Track all unique operations for this cargo (avoid duplicates)
                if (!cargoMap[cargoKey].operations.find(op => op.cargoOperationGuid === cargoOperation.cargoOperationGuid)) {
                    cargoMap[cargoKey].operations.push(cargoOperation);
                }
            });
        });
    }

    // Process transit cargo operations if needed
    if (
        shouldIncludeTransitOperations &&
        activeStowagePlan.transitOperations &&
        activeStowagePlan.transitOperations.length > 0 &&
        selectedStowageRotationPortCall
    ) {
        // Filter transit operations by the selected port call
        const relevantTransitOperations = activeStowagePlan.transitOperations
            .filter((transitPortCall) => transitPortCall.portCallGuid === selectedStowageRotationPortCall.portCallGuid)
            .flatMap((transitPortCall) => transitPortCall.berthCalls[0]?.cargoOperations ?? [])
            .filter(Boolean);

        if (activeStowagePlan.transitStowageItems && activeStowagePlan.transitStowageItems.length > 0) {
            // Only process transit stowage items related to the filtered transit operations
            activeStowagePlan.transitStowageItems.forEach((transitStowage) => {
                if (transitStowage.cargoOperation) {
                    // Check if this stowage item belongs to one of our filtered transit operations
                    const matchingOperation = relevantTransitOperations.find(
                        (op) => op.cargoOperationGuid === transitStowage.cargoOperation?.cargoOperationGuid
                    );

                    if (!matchingOperation || !transitStowage.tankId) return;

                    // Find the tank for this transit stowage
                    const tank = tanks.find((t) => t.tankId === transitStowage.tankId);
                    if (!tank) return;

                    // Find cargo from the matching operation
                    if (!matchingOperation.cargoGuid) return;

                    // Find the cargo from the activeStowagePlan.cargoes
                    const cargo = activeStowagePlan.cargoes.find((c) => c.cargoGuid === matchingOperation.cargoGuid);
                    if (!cargo) return;

                    // Create a CargoContent structure
                    const cargoContent: CargoContent = {
                        cargoOperation: matchingOperation,
                        cargo: cargo,
                        weight: new Decimal(0),
                        weightInTransit: new Decimal(0),
                        billOfLadingWeight: new Decimal(0),
                        stowage: transitStowage,
                    };

                    // Generate a unique key for this transit cargo
                    const cargoKey = getFormattedCargoNumber(cargo.voyageNumber, cargo.cargoNumber, isManualCargo(cargo)) + '-T'; // Add a suffix to distinguish transit cargoes

                    // Add to map or update existing entry
                    if (!cargoMap[cargoKey]) {
                        cargoMap[cargoKey] = {
                            cargoContent: cargoContent,
                            tanks: new Set<string>(),
                            operations: [],
                        };
                    }

                    // Add tank to the list of tanks for this transit cargo
                    cargoMap[cargoKey].tanks.add(tank.odfjellTankName);
                    // Track all unique operations for this cargo (avoid duplicates)
                    if (!cargoMap[cargoKey].operations.find(op => op.cargoOperationGuid === matchingOperation.cargoOperationGuid)) {
                        cargoMap[cargoKey].operations.push(matchingOperation);
                    }
                }
            });
        }
    }

    return cargoMap;
};

// Helper function to convert the cargo map to report contents
const convertCargoMapToReportContents = (
    cargoMap: Record<string, { cargoContent: CargoContent; tanks: Set<string>; operations: RotationCargoOperation[] }>,
    activeStowagePlan: StowagePlan
): ManifestPdfReportContent[] => {
    return Object.entries(cargoMap).map(([cargoKey, data]) => {
        const { cargoContent, tanks, operations } = data;
        const cargoOperation = cargoContent.stowage.cargoOperation;
        const cargo = cargoContent.cargo;
        const product = cargo?.product;

        // Calculate total ship figure across all operations for this cargo
        let totalShipFigure = new Decimal(0);
        let totalBillOfLadingFigure = new Decimal(0);

        // Sum ship figures from all operations for this cargo
        operations.forEach((operation) => {
            const opShipFigure = getShipFigureForOperationType(activeStowagePlan, operation);

            if (opShipFigure) {
                totalShipFigure = totalShipFigure.plus(opShipFigure);
            }
            if (operation.billOfLadingWeight) {
                totalBillOfLadingFigure = totalBillOfLadingFigure.plus(operation.billOfLadingWeight);
            }
        });

        const billOfLadingFigure = totalBillOfLadingFigure.gt(0) ? totalBillOfLadingFigure.toNumber() : null;
        const shipFigure = totalShipFigure.gt(0) ? totalShipFigure : null;

        const cargoTemperature = getCargoTemperature(activeStowagePlan, cargo);
        const litreWeight = roundTo3Decimals(getCorrectedLitreWeight(cargoTemperature, cargo)?.toNumber() ?? 0).toString();
        const heating = getCorrectedVoyageTemperatureRange(cargo);

        // Determine if this is a transit cargo (identified by the -T suffix in the key)
        const isTransit = cargoKey.endsWith('-T');
        let operationType = '';

        if (isTransit) {
            operationType = 'T'; // Transit
        } else if (cargoOperation?.operationType) {
            operationType = getOperationTypeLetter(cargoOperation.operationType);
        }

        return {
            cargoNumber: getFormattedCargoNumber(cargo.voyageNumber, cargo?.cargoNumber, isManualCargo(cargo)) || '',
            tradeName: product?.tradeName || '',
            operationType: operationType,
            shipFigure: shipFigure ? roundTo3Decimals(shipFigure.toNumber()).toString() : '0',
            billOfLadingFigure: billOfLadingFigure ? roundTo3Decimals(billOfLadingFigure).toString() : 'N/A',
            litreWeight: litreWeight.toString() ?? 'N/A',
            flashPoint: product?.flashPointRange?.max?.toString() || '',
            heating: heating || 'N/A',
            un: cargo.product.unCode?.toString() || '',
            imdg: cargo.product.imdgCodeClass?.classification,
            marpol: product?.category.marpolPollutionCategory ? getMarpolPollutionCategoryString(product?.category.marpolPollutionCategory) : '',
            uscg: cargo.product.uscgGroup.number?.toString() || '',
            loadPorts: getFormattedLoadPorts(cargo),
            unloadingPorts: getFormattedUnloadPorts(cargo),
            stowedInTanks: Array.from(tanks).join(', '),
        };
    });
};

// Helper function to sort report contents by cargo number
const sortReportContentsByCargoNumber = (manifestReportContents: ManifestPdfReportContent[]): ManifestPdfReportContent[] => {
    return [...manifestReportContents].sort((a, b) => {
        const aNumber = extractCargoNumber(a.cargoNumber);
        const bNumber = extractCargoNumber(b.cargoNumber);
        return aNumber - bNumber;
    });
};

export const getCargoInTanksList = (
    tankPlan: TankPlan,
    activeStowagePlan: StowagePlan,
    selectedStowageRotationPortCall: RotationPortCall,
    selectedStowageRotationBerthCall: RotationBerthCall,
    tankPlanCondition: TankPlanCondition,
    selectedTankIds: number[] | null = null
): ManifestPdfReportContent[] => {
    // Get all tanks from the tank plan
    const allTanks = getAllTanks(tankPlan);

    // Filter tanks if specific tanks are selected
    const tanks = selectedTankIds && selectedTankIds.length > 0 ? allTanks.filter((tank) => selectedTankIds.includes(tank.tankId)) : allTanks;

    // Build cargo map from tanks
    const cargoMap = buildCargoMapFromTanks(
        tanks,
        activeStowagePlan,
        selectedStowageRotationPortCall,
        selectedStowageRotationBerthCall,
        tankPlanCondition
    );

    // Convert cargo map to report contents
    const reportContents = convertCargoMapToReportContents(cargoMap, activeStowagePlan);

    // Sort report contents by cargo number
    return sortReportContentsByCargoNumber(reportContents);
};

// Extract loading ports as a comma-separated list
const getFormattedLoadPorts = (cargo: any): string => {
    if (!cargo?.loadingPortNames || cargo.loadingPortNames.length === 0) return '';
    return cargo.loadingPortNames.map((port) => port.name).join(', ');
};

// Extract unloading ports as a comma-separated list
const getFormattedUnloadPorts = (cargo: any): string => {
    if (!cargo?.unloadingPortNames || cargo.unloadingPortNames.length === 0) return '';
    return cargo.unloadingPortNames.map((port) => port.name).join(', ');
};

export const getTankIdsWhereCharterersHaveCargo = (
    tankPlan: TankPlan,
    activeStowagePlan: StowagePlan,
    selectedStowageRotationPortCall: RotationPortCall,
    selectedStowageRotationBerthCall: RotationBerthCall,
    selectedCharterers: string[]
): number[] => {
    const _cargoInTanksList = getCargoContentInTanksList(
        tankPlan,
        activeStowagePlan,
        selectedStowageRotationPortCall,
        selectedStowageRotationBerthCall,
        null
    );

    // Filter cargo contents based on selected charterers
    const filteredCargoContents = _cargoInTanksList.filter((cargoContent) => {
        const chartererName = getChartererName(cargoContent.cargo);
        return selectedCharterers.includes(chartererName);
    });

    // Get unique tank IDs from the filtered cargo contents
    const tankIds = Array.from(new Set(filteredCargoContents.map((cargoContent) => cargoContent.stowage?.tankId)));

    return tankIds;
};

export const createReportFilename = (
    reportSubType: ReportSubType,
    vessel: Vessel,
    selectedStowageRotationPortCall: RotationPortCall,
    selectedStowageRotationBerthCall: RotationBerthCall
): string => {
    const vesselName = vessel?.vesselName || 'Vessel';
    const voyageNumber = selectedStowageRotationPortCall?.voyageNumber || '';
    const portName = selectedStowageRotationPortCall?.portDisplayName || '';
    const berthName = selectedStowageRotationBerthCall?.berthName || '';

    let filename = `${vesselName}-${reportSubType.subType}-${voyageNumber}`;

    if (portName) {
        filename += `-${portName}`;
    }

    if (berthName) {
        filename += ` (${berthName})`;
    }

    return filename;
};

/**
 * Resolves the berth call that should actually be used for a Manifest report subtype:
 * 'port'-scoped subtypes always aggregate the whole port call (ignoring any selected berth),
 * while 'berth'-scoped subtypes (including the berth-scoped Arrival/Departure manifest variants
 * in the Berth reports tab) use whichever berth is currently selected.
 */
export const getEffectiveManifestBerthCall = (
    reportSubType: ReportSubType,
    selectedManifestBerthCall: RotationBerthCall | null
): RotationBerthCall | null => {
    if (reportSubType.reportScope === 'port') {
        return null;
    }

    return selectedManifestBerthCall;
};

/**
 * Builds the filename for a Cargo Manifest report. Unlike the generic `createReportFilename`
 * (which suffixes the berth name in parentheses, e.g. "Port (Berth)"), Manifest reports show
 * both port and berth concatenated as "Port - Berth" so the scope is unambiguous at a glance.
 */
export const createManifestReportFilename = (
    reportSubType: ReportSubType,
    vessel: Vessel,
    selectedStowageRotationPortCall: RotationPortCall,
    selectedManifestBerthCall: RotationBerthCall | null
): string => {
    const vesselName = vessel?.vesselName || 'Vessel';
    const voyageNumber = selectedStowageRotationPortCall?.voyageNumber || '';
    const portName = selectedStowageRotationPortCall?.portDisplayName || '';
    const berthCall = getEffectiveManifestBerthCall(reportSubType, selectedManifestBerthCall);
    const berthName = berthCall?.berthName || '';

    let filename = `${vesselName}-${reportSubType.subType}-${voyageNumber}`;

    if (portName && berthName) {
        filename += `-${portName} - ${berthName}`;
    } else if (portName) {
        filename += `-${portName}`;
    }

    return filename;
};

export const createTankPlanReport = (
    reportSubType: ReportSubType,
    vessel: Vessel | null,
    selectedPortCall: RotationPortCall | null,
    selectedBerthCall: RotationBerthCall | null,
    fileName: string
): TankPlanReportGenerationData => {
    const reportOptions: ReportGenerationOptions = {
        fileName,
        orientation: REPORT_TYPE_ORIENTATION[reportSubType.reportType],
    };

    const baseTankPlanData: TankplanReportData = {
        reportType: REPORT_TYPE.TANK_PLANS,
        vessel,
        selectedPortCall,
        selectedBerthCall,
        imgData: '',
        imgWidth: 0,
        imgHeight: 0,
        headerTitle: reportSubType.headerTitle,
    };

    return {
        reportSubType,
        baseData: baseTankPlanData,
        options: reportOptions,
    };
};
